import { useEffect, useState, useCallback, useRef } from "react";
import useTestId from "../../hooks/useTestId";
import useAnalytics from "../../hooks/useAnalytics";

export const useScaleWithGestures = () => {
    const testId = useTestId();
    const { analytics, updateAnalytics } = useAnalytics();
    const defaultScale = 1;
    const [scale, setScale] = useState(defaultScale);
    const [initialPinchDistance, setInitialPinchDistance] = useState<
        number | null
    >(null);
    const [initialScale, setInitialScale] = useState<number>(1);
    const scaleTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        if (analytics?.scale) {
            setScale(analytics.scale);
        }
    }, [analytics?.scale]);

    const setScaleHandler = useCallback(
        (newScale: number) => {
            setScale(newScale);

            if (scaleTimeoutRef.current) {
                clearTimeout(scaleTimeoutRef.current);
            }

            scaleTimeoutRef.current = setTimeout(() => {
                updateAnalytics({ scale: newScale });
            }, 1000);
        },
        [testId]
    );

    useEffect(() => {
        const editorElement = document.querySelector(".editor");
        if (!editorElement) return;

        const getTouchDistance = (touches: TouchList) => {
            if (touches.length < 2) return 0;
            const dx = touches[0].clientX - touches[1].clientX;
            const dy = touches[0].clientY - touches[1].clientY;
            return Math.sqrt(dx * dx + dy * dy);
        };

        const handleWheel = (e: WheelEvent) => {
            if (e.ctrlKey || e.metaKey) {
                e.preventDefault();
                const delta = e.deltaY > 0 ? -0.05 : 0.05;
                const newScale = Math.min(Math.max(scale + delta, 0.5), 1.5);
                setScaleHandler(newScale);
            }
        };

        // Safari gesture events
        const handleGestureStart = (e: any) => {
            e.preventDefault();
            setInitialScale(scale);
        };

        const handleGestureChange = (e: any) => {
            e.preventDefault();
            const newScale = Math.min(
                Math.max(initialScale * e.scale, 0.5),
                1.5
            );
            setScaleHandler(newScale);
        };

        // Touch events for other browsers
        const handleTouchStart = (e: TouchEvent) => {
            if (e.touches.length === 2) {
                e.preventDefault();
                setInitialPinchDistance(getTouchDistance(e.touches));
                setInitialScale(scale);
            }
        };

        const handleTouchMove = (e: TouchEvent) => {
            if (e.touches.length === 2 && initialPinchDistance !== null) {
                e.preventDefault();
                const currentDistance = getTouchDistance(e.touches);
                const scaleFactor = currentDistance / initialPinchDistance;
                const newScale = Math.min(
                    Math.max(initialScale * scaleFactor, 0.5),
                    1.5
                );
                setScaleHandler(newScale);
            }
        };

        const handleTouchEnd = () => {
            setInitialPinchDistance(null);
        };

        // Add all event listeners
        editorElement.addEventListener("wheel", handleWheel as EventListener, {
            passive: false,
        });
        editorElement.addEventListener(
            "gesturestart",
            handleGestureStart as EventListener,
            {
                passive: false,
            }
        );
        editorElement.addEventListener(
            "gesturechange",
            handleGestureChange as EventListener,
            {
                passive: false,
            }
        );
        editorElement.addEventListener(
            "touchstart",
            handleTouchStart as EventListener,
            {
                passive: false,
            }
        );
        editorElement.addEventListener(
            "touchmove",
            handleTouchMove as EventListener,
            {
                passive: false,
            }
        );
        editorElement.addEventListener(
            "touchend",
            handleTouchEnd as EventListener
        );

        // Cleanup
        return () => {
            editorElement.removeEventListener(
                "wheel",
                handleWheel as EventListener
            );
            editorElement.removeEventListener(
                "gesturestart",
                handleGestureStart as EventListener
            );
            editorElement.removeEventListener(
                "gesturechange",
                handleGestureChange as EventListener
            );
            editorElement.removeEventListener(
                "touchstart",
                handleTouchStart as EventListener
            );
            editorElement.removeEventListener(
                "touchmove",
                handleTouchMove as EventListener
            );
            editorElement.removeEventListener(
                "touchend",
                handleTouchEnd as EventListener
            );
        };
    }, [scale, setScaleHandler, initialScale, initialPinchDistance]);

    return [scale, setScaleHandler] as const;
};
